import { useCallback } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { itemAtomFamily, unsavedChangesAtom } from '@/atoms/_editorAtoms'
import { itemControlStateAtomFamily } from '@/atoms/itemControlAtoms'
import { useEditorTextBoxDelete } from '@/hooks/editor/useEditorTextBoxDelete'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { PageItemErrorBoundary } from './PageItemErrorBoundary'

const Fallback = styled.div`
  position: absolute;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  border: 2px dashed #b22234;
  background: rgba(255, 255, 255, 0.85);
  color: #b22234;
  font-family: system-ui, sans-serif;
  font-size: 12px;
  line-height: 1.3;
  text-align: center;
  overflow: hidden;
  z-index: 1;
`

const Actions = styled.div`
  display: flex;
  gap: 6px;
`

const Action = styled.button`
  border: 1px solid #b22234;
  border-radius: 3px;
  background: #fff;
  color: #b22234;
  font: inherit;
  padding: 2px 8px;
  cursor: pointer;
`

const asNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

/**
 * One page item under its own net: an item that throws while it paints or
 * fits gives way to a placeholder at its frame, with the toast and the log
 * line every handled error gets, and the rest of the page stays as it was —
 * every other item, the toolbar, Save and Back. The placeholder offers a
 * retry, and the remove the overlay's trash icon does, so the item that
 * cannot be drawn can still be taken off the page and the page saved.
 */
const PageItemGuard = ({
  id,
  children,
}: {
  id: string
  children: ReactNode
}) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('PageItem')
  const item = useRecoilValue(itemAtomFamily(id)) as
    | { x?: unknown; y?: unknown; width?: unknown; height?: unknown }
    | null
    | undefined
  const resetItem = useResetRecoilState(itemAtomFamily(id))
  const resetControlState = useResetRecoilState(itemControlStateAtomFamily(id))
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)
  const deleteEditorTextBox = useEditorTextBoxDelete()
  const onError = useCallback(
    (error: Error, info: ErrorInfo) => {
      handleError(t('editor.itemFallback.toast'), error)
      console.error(`page item ${id} could not be drawn`, info.componentStack)
    },
    [handleError, id, t],
  )
  const remove = useCallback(() => {
    void deleteEditorTextBox(id)
    resetControlState()
    resetItem()
    setUnsavedChanges(true)
  }, [deleteEditorTextBox, id, resetControlState, resetItem, setUnsavedChanges])
  return (
    <PageItemErrorBoundary
      onError={onError}
      fallback={(_error, retry) => (
        <Fallback
          data-page-item-fallback={id}
          data-item-id={id}
          style={{
            left: asNumber(item?.x, 0),
            top: asNumber(item?.y, 0),
            width: Math.max(120, asNumber(item?.width, 120)),
            height: Math.max(48, asNumber(item?.height, 48)),
          }}
        >
          <span>{t('editor.itemFallback.message')}</span>
          <Actions>
            <Action type="button" onClick={retry}>
              {t('editor.itemFallback.retry')}
            </Action>
            <Action type="button" onClick={remove}>
              {t('editor.itemFallback.remove')}
            </Action>
          </Actions>
        </Fallback>
      )}
    >
      {children}
    </PageItemErrorBoundary>
  )
}

export default PageItemGuard
